import { RefreshCw, Printer } from 'lucide-react'
import React, { useRef, useState, useEffect, memo, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import styles from '~/styles/NodeDocument.module.css'
import MarkdownRenderer from '../markdown-renderer/MarkdownRenderer'

// Utility function to handle scrolling
const handleScroll = (element, deltaY) => {
  if (!element) return

  const { scrollTop, scrollHeight, clientHeight } = element
  const isAtTop = scrollTop <= 0
  const isAtBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 2
  const isScrollingUp = deltaY < 0
  const isScrollingDown = deltaY > 0

  if ((isScrollingUp && !isAtTop) || (isScrollingDown && !isAtBottom)) {
    element.scrollTop += deltaY
    return true // Event was handled
  }
  return false // Let it bubble for panning
}

// Update the interface
interface DocumentTabsProps {
  contentMarkdown: string
  onContentChange: (content: string) => void
  nodeId: string
  context: string
  onContextChange: (context: string) => void
  linkedKnowledge: string
  isMarkdownExpanded: boolean
  markdownHeight: string
  textareaHeight: string
  nodebaseHeight: string
  markdownFormatVisible: boolean
  onSectionSelect: (
    sectionId: string,
    sectionName: string,
    event: React.MouseEvent
  ) => void
  sectionStates: Record<string, "none" | "accepted" | "rejected" | "pending">
  processingStates?: Record<string, boolean>
  sectionReviews?: Record<string, {
    originalContent: string
    suggestedContent: string
    commandType: string
  }>
  onAcceptReview?: (sectionId: string) => void
  onRejectReview?: (sectionId: string) => void
  markdownPreviewRef: React.RefObject<HTMLDivElement>
  onRefreshLinkedKnowledge: () => void
  onLinkedKnowledgeChange: (linkedKnowledge: string) => void
  researchTopic?: string
  onResearchTopicChange?: (topic: string) => void
  protectedSections?: Record<string, boolean>
  sectionExplanations?: Record<string, string>
  sectionExplanationStates?: Record<string, boolean>
  onToggleExplanation?: (sectionId: string) => void
  printSettings?: {
    headerText: string
    footerText: string
  }
  onPrintSettingsChange?: (settings: { headerText: string; footerText: string }) => void
  data?: {
    label?: string
    assignedEmails?: string[]
  }
}

const LinkedKnowledgeTab = React.memo(
  ({
    linkedKnowledge,
    onRefreshLinkedKnowledge,
    nodebaseHeight,
    isMarkdownExpanded,
  }: {
    linkedKnowledge: string
    onRefreshLinkedKnowledge: () => void
    nodebaseHeight: string
    isMarkdownExpanded: boolean
  }) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
      const container = scrollContainerRef.current
      if (!container) return

      const handleWheelEvent = (e: WheelEvent) => {
        if (handleScroll(container, e.deltaY)) {
          e.preventDefault()
          e.stopPropagation()
        }
      }

      container.addEventListener('wheel', handleWheelEvent, { passive: false })
      return () => {
        container.removeEventListener('wheel', handleWheelEvent)
      }
    }, [])

    return (
      <div className="p-3 h-full">
        <div className="flex justify-between mb-2">
          <div className="text-sm text-gray-500">
            This panel shows knowledge linked from other nodes.
          </div>
          <Button
            onClick={(e) => {
              e.stopPropagation()
              onRefreshLinkedKnowledge()
            }}
            variant="outline"
            size="sm"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>
        <div
          ref={scrollContainerRef}
          className="overflow-auto markdown-body p-2 border rounded h-auto scrollable-content"
          style={{
            height: isMarkdownExpanded ? 'auto' : nodebaseHeight,
            maxHeight: isMarkdownExpanded ? 'none' : 'auto',
            border: '1px solid #ccc',
            padding: '8px',
            overflowY: 'auto',
          }}
        >
          {linkedKnowledge ? (
            <div className={`${styles.markdownPreview} scrollable-content`}>
              <MarkdownRenderer
                markdown={`\`\`\`markdown\n${linkedKnowledge}\n\`\`\``}
              />
            </div>
          ) : (
            <div className="text-gray-400 italic p-2">
              No linked knowledge available. Connect this document to other
              nodes using the Data Settings.
            </div>
          )}
        </div>
      </div>
    )
  }
)

// Add this function before the DocumentTabs component
const processTemplate = (template: string, variables: Record<string, string | number>) => {
  let processed = template;
  
  // Replace all variables
  Object.entries(variables).forEach(([key, value]) => {
    const regex = new RegExp(`\\[%${key}%\\]`, 'g');
    processed = processed.replace(regex, String(value));
  });
  
  // Process bold text
  processed = processed.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  
  return processed;
};

const DocumentTabs: React.FC<DocumentTabsProps> = ({
  contentMarkdown,
  onContentChange,
  nodeId,
  context,
  onContextChange,
  linkedKnowledge,
  isMarkdownExpanded,
  markdownHeight,
  textareaHeight,
  nodebaseHeight,
  markdownFormatVisible,
  onSectionSelect,
  sectionStates,
  processingStates = {},
  sectionReviews = {},
  onAcceptReview,
  onRejectReview,
  markdownPreviewRef,
  onRefreshLinkedKnowledge,
  onLinkedKnowledgeChange,
  researchTopic = '',
  onResearchTopicChange,
  protectedSections = {},
  sectionExplanations = {},
  sectionExplanationStates = {},
  onToggleExplanation,
  printSettings = { headerText: '', footerText: '' },
  onPrintSettingsChange,
  data,
}) => {
  const [activeTab, setActiveTab] = useState('document')
  const [localHeaderText, setLocalHeaderText] = useState(printSettings?.headerText || '')
  const [localFooterText, setLocalFooterText] = useState(printSettings?.footerText || '')
  const contentRefs = useRef<{
    document: HTMLDivElement | null
    markdown: HTMLDivElement | null
    markdownEditable: HTMLTextAreaElement | null
    context: HTMLTextAreaElement | null
    researchTopic: HTMLTextAreaElement | null
    linkedKnowledge: HTMLDivElement | null
    printable: HTMLDivElement | null
  }>({
    document: null,
    markdown: null,
    markdownEditable: null,
    context: null,
    researchTopic: null,
    linkedKnowledge: null,
    printable: null,
  })

  const [localContext, setLocalContext] = useState(context)
  const [localResearchTopic, setLocalResearchTopic] = useState(researchTopic)
  const [localLinkedKnowledge, setLocalLinkedKnowledge] = useState(linkedKnowledge)
  const [localContent, setLocalContent] = useState(contentMarkdown) // Restore localContent state for markdown editable

  // Sync local state with props
  useEffect(() => {
    setLocalContext(context)
    if (researchTopic !== undefined) setLocalResearchTopic(researchTopic)
    setLocalLinkedKnowledge(linkedKnowledge)
    setLocalContent(contentMarkdown)
  }, [contentMarkdown, context, linkedKnowledge, researchTopic])

  // Sync local state with props when they change
  useEffect(() => {
    setLocalHeaderText(printSettings?.headerText || '')
    setLocalFooterText(printSettings?.footerText || '')
  }, [printSettings])

  const handleTabChange = (value: string) => {
    setActiveTab(value)
  }

  // Wheel event listener management with retry mechanism
  useEffect(() => {
    const attachListeners = () => {
      const handleWheel = (e: WheelEvent) => {
        const target = e.currentTarget as HTMLElement | HTMLTextAreaElement
        if (handleScroll(target, e.deltaY)) {
          e.preventDefault()
          e.stopPropagation()
        }
      }

      const elements = Object.entries(contentRefs.current)
        .filter(([_, el]) => el !== null)
        .map(([_, el]) => el) as (HTMLDivElement | HTMLTextAreaElement)[]      // Log for debugging
      console.log(
        'DocumentTabs: Attaching scroll listeners to elements:',
        elements.map((el) => ({
          tagName: el.tagName,
          className: el.className,
          id: el.id || 'no-id',
          isConnected: el.isConnected,
          offsetParent: el.offsetParent?.tagName || 'null'
        }))
      )

      elements.forEach((el) => {
        el.addEventListener('wheel', handleWheel, { passive: false })
      })

      return () => {
        elements.forEach((el) => {
          el.removeEventListener('wheel', handleWheel)
        })
      }
    }

    // Initial attachment
    let cleanup = attachListeners()

    // Retry after a short delay to catch late-mounted elements
    const timeout = setTimeout(() => {
      cleanup()
      cleanup = attachListeners()
    }, 100) // 100ms delay to ensure DOM is settled

    return () => {
      clearTimeout(timeout)
      cleanup()
    }
  }, [activeTab]) // Trigger on tab change

  const handleContextBlur = () => {
    if (localContext !== context) onContextChange(localContext)
  }

  const handleResearchTopicBlur = () => {
    if (onResearchTopicChange && localResearchTopic !== researchTopic) {
      onResearchTopicChange(localResearchTopic)
    }
  }

  const handleLinkedKnowledgeBlur = () => {
    if (onLinkedKnowledgeChange && localLinkedKnowledge !== linkedKnowledge) {
      onLinkedKnowledgeChange(localLinkedKnowledge)
    }
  }

  // Only sync on blur
  const handleContentBlur = () => {
    if (localContent !== contentMarkdown) onContentChange(localContent)
  }

  const handleHeaderTextBlur = () => {
    if (onPrintSettingsChange) {
      onPrintSettingsChange({
        ...printSettings,
        headerText: localHeaderText
      })
    }
  }

  const handleFooterTextBlur = () => {
    if (onPrintSettingsChange) {
      onPrintSettingsChange({
        ...printSettings,
        footerText: localFooterText
      })
    }
  }

  const cleanMarkdownForPrinting = (markdown: string) => {
    return markdown
      .replace(/:::\s*section\s+.*$/gm, '')
      .replace(/^:::$/gm, '')
      .replace(/\n\n\n+/gm, '\n\n')
      .trim()
  }

  const handlePrint = () => {
    const headerText = printSettings.headerText || '';
    const footerText = printSettings.footerText || '';
    
    // Create a new window with a unique name to prevent caching
    const printWindow = window.open('', `print_${Date.now()}`)
    if (!printWindow) {
      console.error('Failed to open print window')
      return
    }

    const printableContent = document.getElementById('printable-content')?.innerHTML
    if (!printableContent) {
      printWindow.close()
      return
    }

    // Watermark text and date
    const now = new Date();
    const date = now.toISOString().slice(0, 10);
    const time = now.toTimeString().slice(0, 5);
    const watermarkText = `DRAFT\\A Spechops\\A ${date} ${time}`;

    // Process header and footer with variables
    const headerVariables = {
      DATE: new Date().toLocaleDateString(),
      TIME: new Date().toLocaleTimeString(),
      YEAR: new Date().getFullYear(),
      MONTH: new Date().toLocaleString('default', { month: 'long' }),
      DAY: new Date().getDate(),
      DOCUMENT_TITLE: data?.label || 'Untitled Document',
      AUTHOR: data?.assignedEmails?.[0] || 'Unknown',
      VERSION: '1.0',
      COMPANY: 'Spechops'
    };

    const footerVariables = {
      CP: '1', // Current page - will be updated by the browser
      MP: '1', // Max pages - will be updated by the browser
      DATE: new Date().toLocaleDateString(),
      TIME: new Date().toLocaleTimeString(),
      YEAR: new Date().getFullYear(),
      DOCUMENT_TITLE: data?.label || 'Untitled Document',
      AUTHOR: data?.assignedEmails?.[0] || 'Unknown',
      VERSION: '1.0',
      COMPANY: 'Spechops'
    };

    const processedHeader = processTemplate(headerText, headerVariables);
    const processedFooter = processTemplate(footerText, footerVariables);

    // Write the content to the new window
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Print Document</title>
          <style>
            body {
              font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Inter', Roboto, sans-serif;
              line-height: 1.5;
              font-weight: lighter;
              padding: 0;
              margin: 0;
              position: relative;
            }

            /* Page setup */
            @page {
              size: A4;
              margin: 0;
            }

            /* Content container */
            .page-content {
              position: relative;
              padding: 0;
              margin: 0;
              box-sizing: border-box;
            }

            /* Header and Footer styles */
            .print-header {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              height: 3cm;
              text-align: center;
              font-size: 1.2rem;
              padding: 0.5rem;
              border-bottom: 1px solid #ccc;
              background: white;
              z-index: 1000;
            }

            .print-footer {
              position: fixed;
              bottom: 0;
              left: 0;
              right: 0;
              height: 3cm;
              text-align: center;
              font-size: 1rem;
              padding: 0.5rem;
              border-top: 1px solid #ccc;
              background: white;
              z-index: 1000;
            }

            /* Main content area */
            #printable-main-content {
              margin-top: 3cm;
              margin-bottom: 3cm;
              padding: 1cm;
              min-height: calc(100vh - 6cm);
              box-sizing: border-box;
            }

            /* Content pagination rules */
            .content-section {
              break-inside: auto;
              page-break-inside: auto;
            }

            h1, h2, h3, h4, h5, h6 {
              break-before: auto;
              page-break-before: auto;
              margin-top: 1.5em;
              margin-bottom: 0.5em;
              font-weight: normal;
            }

            p {
              margin-bottom: 1em;
              orphans: 3;
              widows: 3;
            }

            /* Prevent orphaned list items */
            li {
              break-inside: avoid;
              page-break-inside: avoid;
            }

            /* Table handling */
            table {
              break-inside: avoid;
              page-break-inside: avoid;
              width: 100%;
              margin-bottom: 1em;
            }

            /* Image handling */
            img {
              max-width: 100%;
              height: auto;
              break-inside: avoid;
              page-break-inside: avoid;
            }

            /* Watermark */
            body::before {
              content: '${watermarkText}';
              position: fixed;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%) rotate(-20deg);
              font-size: 5rem;
              color: #cccccc33;
              z-index: 0;
              pointer-events: none;
              white-space: pre-line;
              width: 100vw;
              text-align: center;
              opacity: 0.25;
              user-select: none;
            }

            body > * {
              position: relative;
              z-index: 1;
            }

            /* Print-specific styles */
            @media print {
              html, body {
                height: auto;
                margin: 0;
                padding: 0;
              }

              .page-content {
                position: relative;
                min-height: 100vh;
              }

              #printable-main-content {
                position: relative;
                margin-top: 3cm;
                margin-bottom: 3cm;
                padding: 1cm;
                min-height: calc(100vh - 6cm);
              }

              /* Ensure content flows across pages */
              .content-section {
                break-inside: auto;
                page-break-inside: auto;
              }

              /* Force page breaks before major sections if they don't fit */
              h1, h2 {
                break-before: auto;
                page-break-before: auto;
              }

              /* Keep these elements together */
              table, img, pre {
                break-inside: avoid;
                page-break-inside: avoid;
              }

              /* Ensure headers and footers stay fixed */
              .print-header {
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                height: 3cm;
                background: white;
                z-index: 1000;
              }

              .print-footer {
                position: fixed;
                bottom: 0;
                left: 0;
                right: 0;
                height: 3cm;
                background: white;
                z-index: 1000;
              }

              /* Update page numbers */
              .print-footer::after {
                content: counter(page);
              }
            }
          </style>
        </head>
        <body>
          ${processedHeader ? `<div class="print-header">${processedHeader}</div>` : ''}
          <div class="page-content">
            <div id="printable-main-content">
              ${printableContent}
            </div>
          </div>
          ${processedFooter ? `<div class="print-footer">${processedFooter}</div>` : ''}
          <script>
            window.onload = function() {
              // Add content-section class to major content blocks
              const contentBlocks = document.querySelectorAll('h1, h2, h3, table, img');
              contentBlocks.forEach(block => {
                const section = document.createElement('div');
                section.className = 'content-section';
                block.parentNode.insertBefore(section, block);
                section.appendChild(block);
              });

              // Update page numbers
              const totalPages = Math.ceil(document.body.scrollHeight / window.innerHeight);
              document.querySelectorAll('.print-footer').forEach(footer => {
                footer.innerHTML = footer.innerHTML
                  .replace('[%CP%]', '1')
                  .replace('[%MP%]', totalPages.toString());
              });

              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `)
    printWindow.document.close()
  }

  // Create a callback ref for the markdown preview
  const setMarkdownPreviewRef = useCallback((el: HTMLDivElement | null) => {
    if (markdownPreviewRef && el) {
      (markdownPreviewRef as React.MutableRefObject<HTMLDivElement | null>).current = el
    }
    contentRefs.current.document = el
  }, [markdownPreviewRef])

  return (
    <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full ">
      <TabsList className={`grid w-full grid-flow-col h-12`}>
        <TabsTrigger value="document" className={`${styles.documentTabsTitle}`}>Document</TabsTrigger>
        {markdownFormatVisible && (
          <TabsTrigger value="markdown" className={`${styles.documentTabsTitle}`}>Markdown</TabsTrigger>
        )}
        <TabsTrigger value="markdownEditable" className={`${styles.documentTabsTitle}`}>
          Markdown (editable raw)
        </TabsTrigger>
        <TabsTrigger value="context" className={`${styles.documentTabsTitle}`}>Context</TabsTrigger>
        {onResearchTopicChange && (
          <TabsTrigger value="researchTopic" className={`${styles.documentTabsTitle}`}>Research Topic</TabsTrigger>
        )}
        <TabsTrigger value="linkedKnowledge" className={`${styles.documentTabsTitle}`}>Linked Knowledge</TabsTrigger>
        <TabsTrigger value="printable" className={`${styles.documentTabsTitle}`}>Printable</TabsTrigger>
      </TabsList>
      <TabsContent value="document">
        <div
          ref={setMarkdownPreviewRef}
          className={`${styles.markdownPreview} scrollable-content`}
          style={{
            height: isMarkdownExpanded ? markdownHeight : nodebaseHeight,
            maxHeight: isMarkdownExpanded ? 'none' : 'auto',
            border: '1px solid #ccc',
            padding: '8px',
            overflowY: 'auto',
          }}
        >
          {' '}
          <MarkdownRenderer
            markdown={contentMarkdown.trim()}
            nodeId={nodeId}
            onSectionSelect={onSectionSelect}
            sectionStates={sectionStates}
            processingStates={processingStates}
            sectionReviews={sectionReviews}
            onAcceptReview={onAcceptReview}
            onRejectReview={onRejectReview}
            protectedSections={protectedSections}
            sectionExplanations={sectionExplanations}
            sectionExplanationStates={sectionExplanationStates}
            onToggleExplanation={onToggleExplanation}
          />
        </div>
      </TabsContent>
      {markdownFormatVisible && (
        <TabsContent value="markdown">
          <div
            ref={(el) => (contentRefs.current.markdown = el)}
            className={`${styles.markdownPreview} scrollable-content`}
            style={{
              height: isMarkdownExpanded ? markdownHeight : nodebaseHeight,
              maxHeight: isMarkdownExpanded ? 'none' : 'auto',
              border: '1px solid #ccc',
              padding: '8px',
              overflowY: 'auto',
            }}
          >
            {' '}
            <MarkdownRenderer
              markdown={`\`\`\`markdown\n${contentMarkdown.trim()}\n\`\`\``}
              sectionStates={sectionStates}
              processingStates={processingStates}
              sectionExplanations={sectionExplanations}
              sectionExplanationStates={sectionExplanationStates}
              onToggleExplanation={onToggleExplanation}
            />
          </div>
        </TabsContent>
      )}
      <TabsContent value="markdownEditable">
        <Textarea
          ref={(el) => (contentRefs.current.markdownEditable = el)}
          className="textArea nodrag scrollable-content"
          placeholder="Content Markdown: Enter the document content."
          value={localContent}
          onChange={(e) => setLocalContent(e.target.value)}
          onBlur={handleContentBlur}
          style={{
            width: '100%',
            height: isMarkdownExpanded ? textareaHeight : nodebaseHeight,
            maxHeight: isMarkdownExpanded ? 'none' : 'auto',
          }}
        />
      </TabsContent>
      <TabsContent value="context">
        <Textarea
          ref={(el) => (contentRefs.current.context = el)}
          className="textArea nodrag scrollable-content"
          placeholder="Enter document context information here"
          value={localContext}
          onChange={(e) => setLocalContext(e.target.value)}
          onBlur={handleContextBlur}
          style={{
            width: '100%',
            height: isMarkdownExpanded ? textareaHeight : nodebaseHeight,
            maxHeight: isMarkdownExpanded ? 'none' : 'auto',
          }}
        />
      </TabsContent>
      {onResearchTopicChange && (
        <TabsContent value="researchTopic">
          <Textarea
            ref={(el) => (contentRefs.current.researchTopic = el)}
            className="textArea nodrag scrollable-content"
            placeholder="Enter research topic"
            value={localResearchTopic}
            onChange={(e) => setLocalResearchTopic(e.target.value)}
            onBlur={handleResearchTopicBlur}
            style={{
              width: '100%',
              height: isMarkdownExpanded ? textareaHeight : nodebaseHeight,
              maxHeight: isMarkdownExpanded ? 'none' : 'auto',
            }}
          />
        </TabsContent>
      )}
      <TabsContent value="linkedKnowledge">
        <LinkedKnowledgeTab
          linkedKnowledge={linkedKnowledge}
          onRefreshLinkedKnowledge={onRefreshLinkedKnowledge}
          nodebaseHeight={nodebaseHeight}
          isMarkdownExpanded={isMarkdownExpanded}
        />
      </TabsContent>
      <TabsContent value="printable">
        <div className="p-3">
          <div className="flex justify-between mb-2">
            <div className="text-sm text-gray-500">
              Clean document view for printing or exporting as PDF.
            </div>
            <Button onClick={handlePrint} variant="outline" size="sm">
              <Printer className="mr-2 h-4 w-4" />
              Print / PDF
            </Button>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Header Text (optional)</label>
            <Textarea
              id="print-header-input"
              className="w-full nodrag"
              placeholder="Enter header text for printing"
              rows={2}
              value={localHeaderText}
              onChange={(e) => setLocalHeaderText(e.target.value)}
              onBlur={handleHeaderTextBlur}
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Footer Text (optional)</label>
            <Textarea
              id="print-footer-input"
              className="w-full nodrag"
              placeholder="Enter footer text for printing"
              rows={2}
              value={localFooterText}
              onChange={(e) => setLocalFooterText(e.target.value)}
              onBlur={handleFooterTextBlur}
            />
          </div>
          <div
            ref={(el) => (contentRefs.current.printable = el)}
            className={`${styles.markdownPreview} border rounded p-4 scrollable-content`}
            style={{
              height: isMarkdownExpanded ? markdownHeight : nodebaseHeight,
              maxHeight: isMarkdownExpanded ? 'none' : 'auto',
              overflow: 'auto',
              backgroundColor: 'white',
            }}
          >
            <div id="printable-content" className="font-sans text-2xl">
              <MarkdownRenderer
                markdown={cleanMarkdownForPrinting(contentMarkdown)}
              />
            </div>
          </div>
        </div>
      </TabsContent>
    </Tabs>
  )
}

export default memo(DocumentTabs)
